// =============================================================================
// ImpersonationBanner — red strip pinned to the bottom of the viewport
// while an admin is impersonating another user. Loud + unmissable on
// purpose: admin must never forget they're acting as someone else.
//
// "Stop Impersonating" opens a branded react-bootstrap modal (matches the
// rest of the app's confirmation styling) rather than a native
// window.confirm, so the dialog stays on-brand even though it sits at the
// very top zIndex of the layout.
//
// Re-checks sessionStorage on every render so it picks up start/stop
// without needing Redux wiring. Drop once near the root of the app shell.
// =============================================================================

import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { stopImpersonation, getImpersonationBannerData } from "../services/impersonationService";

const BANNER_HEIGHT = 44;

const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, userName, userEmail } = getImpersonationBannerData();
  const [showStopModal, setShowStopModal] = useState(false);

  // The bar is fixed, so it would otherwise sit on top of whatever is at the
  // bottom of the page once the user scrolls all the way down.
  useEffect(() => {
    if (!isImpersonating) return;
    const previous = document.body.style.paddingBottom;
    document.body.style.paddingBottom = `${BANNER_HEIGHT}px`;
    return () => {
      document.body.style.paddingBottom = previous;
    };
  }, [isImpersonating]);

  if (!isImpersonating) return null;

  const handleConfirmStop = () => {
    // stopImpersonation does a `window.location.replace("/")` itself, so we
    // don't need to close the modal or update state afterwards.
    stopImpersonation();
  };

  return (
    <>
      <div
        role="alert"
        aria-live="assertive"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
          background: "linear-gradient(90deg, #dc2626, #b91c1c)",
          color: "#fff",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 16,
          fontSize: 13,
          fontWeight: 600,
          boxShadow: "0 -2px 12px rgba(220,38,38,0.35)",
        }}
      >
        <i className="ti ti-user-shield" style={{ fontSize: 18 }} />
        <span style={{ flex: "0 1 auto" }}>
          You are impersonating <strong>{userName || userEmail || "another user"}</strong>
          {userEmail && userName ? <span style={{ opacity: 0.85 }}> ({userEmail})</span> : null}.
          Every action is logged.
        </span>
        <button
          onClick={() => setShowStopModal(true)}
          style={{
            background: "#fff",
            color: "#b91c1c",
            border: "none",
            borderRadius: 6,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className="ti ti-logout" style={{ fontSize: 14 }} />
          Stop Impersonating
        </button>
      </div>

      {/* ===== Stop Impersonation Confirmation Modal =====
           Matches the visual language of the start-impersonation modal but
           swapped to a red/danger theme + logout icon. */}
      <Modal show={showStopModal} onHide={() => setShowStopModal(false)} centered>
        <Modal.Body style={{ padding: 32 }}>
          <div className="text-center">
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#fee2e2",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 16,
              }}
            >
              <i className="ti ti-logout" style={{ fontSize: 28, color: "#dc2626" }} />
            </div>
            <h4 style={{ fontWeight: 700, color: "#1e293b", marginBottom: 8 }}>
              Stop impersonating?
            </h4>
            <p style={{ color: "#64748b", marginBottom: 4 }}>
              You'll return to your own admin account and the page will reload.
            </p>
            {(userName || userEmail) && (
              <p style={{ color: "#94a3b8", fontSize: 12, marginBottom: 0 }}>
                Currently acting as <strong>{userName || userEmail}</strong>
              </p>
            )}
            <div className="d-flex align-items-center justify-content-center mt-4 gap-2">
              <button
                className="btn btn-light"
                onClick={() => setShowStopModal(false)}
                style={{ borderRadius: 8 }}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={handleConfirmStop}
                style={{
                  borderRadius: 8,
                  background: "linear-gradient(135deg, #dc2626, #b91c1c)",
                  color: "#fff",
                  fontWeight: 600,
                  border: "none",
                  padding: "8px 18px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <i className="ti ti-logout" style={{ fontSize: 16 }} />
                Stop &amp; Return
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ImpersonationBanner;
